import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Card, 
  Flex, 
  Heading, 
  Text, 
  View,
  Alert,
  Loader
} from '@aws-amplify/ui-react';
import ProjectForm from './ProjectForm';
import ProjectList from './ProjectList';
import ProjectDetails from './ProjectDetails';
import ParticipantManagement from './ParticipantManagement';
import { useNavigate } from 'react-router-dom';
import corsProxy from '../../utils/corsProxy';

const AdminDashboard = ({ user, projectId: routeProjectId }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [view, setView] = useState(routeProjectId ? 'details' : 'list');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  // Deep link: load project from route param
  useEffect(() => {
    if (routeProjectId && projects.length > 0) {
      const project = projects.find(p => (p.id || p.projectId) === routeProjectId);
      if (project) {
        setSelectedProject(project);
        setView('details');
      }
    }
  }, [routeProjectId, projects]);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await corsProxy.get('/projects');
      const projectsData = response.projects || response;

      if (Array.isArray(projectsData)) {
        setProjects(projectsData);
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (apiError) {
      console.error('Error fetching projects:', apiError);
      setError(`Failed to load projects: ${apiError.message || 'Unknown error'}`);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async (project) => {
    try {
      setLoading(true);
      const response = await corsProxy.get(`/projects/${project.projectId}/export`);

      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(response));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", `${project.name.replace(/\s+/g, '_')}_export.json`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();

      setSuccess('Project data exported successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error exporting project data:', err);
      setError('Failed to export project data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goToList = () => {
    setView('list');
    navigate('/');
  };

  const handleCreateProject = () => {
    setSelectedProject(null);
    setView('create');
  };

  const handleSubmitProject = async (projectData) => {
    try {
      setLoading(true);

      if (projectData.projectId) {
        await corsProxy.put(`/projects/${projectData.projectId}`, projectData);
        setProjects(projects.map(project =>
          project.projectId === projectData.projectId ? projectData : project
        ));
        setSuccess('Project updated successfully');
      } else {
        const response = await corsProxy.post('/projects', projectData);
        setProjects([...projects, response]);
        setSuccess('Project created successfully');
      }

      goToList();
      setTimeout(() => setSuccess(null), 3000);
    } catch (apiError) {
      console.error('Error saving project:', apiError);
      setError(`Error: ${apiError.message || 'Failed to save project'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setView('edit');
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setView('details');
    navigate(`/projects/${project.id || project.projectId}`);
  };

  const handleManageParticipants = (project) => {
    setSelectedProject(project);
    setView('participants');
  };

  const handleDeleteProject = async (projectId) => {
    try {
      setLoading(true);
      await corsProxy.delete(`/projects/${projectId}`);
      setProjects(projects.filter(project => (project.id || project.projectId) !== projectId));
      setSuccess('Project deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (apiError) {
      console.error('Error deleting project:', apiError);
      setError(`Error: ${apiError.message || 'Failed to delete project'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveParticipants = async (participants) => {
    try {
      setLoading(true);

      console.log('Saving participants:', participants);
      console.log('Selected project:', selectedProject);

      // Get project ID - handle both id and projectId properties
      const projectId = selectedProject.projectId || selectedProject.id;

      if (!projectId) {
        setError('Error: Project ID not found');
        return;
      }

      try {
        // Call the API to update participants
        console.log("Making API call to update participants");
        console.log(`API endpoint: /projects/${projectId}/participants`);
        console.log(`API payload:`, { participants });

        // Use our corsProxy utility to make the request
        console.log("Using corsProxy utility");
        const response = await corsProxy.put(`/projects/${projectId}/participants`, { participants });
        
        console.log("API response:", response);
        setSuccess('Participants updated successfully');
      } catch (apiError) {
        console.error('API error details:', apiError);
        setError(`Error: ${apiError.message || 'Failed to save participants'}`);
        return;
      }
      
      // Update the project in the state (do this regardless of API success/failure)
      const updatedProjects = projects.map(project =>
        (project.projectId || project.id) === projectId ? {
          ...project,
          participants: participants.length,
          participantIds: participants.map(p => p.id || p.email)
        } : project
      );
      
      // Update the selected project
      const updatedSelectedProject = {
        ...selectedProject,
        participants: participants.length,
        participantIds: participants.map(p => p.id || p.email)
      };
      
      setProjects(updatedProjects);
      setSelectedProject(updatedSelectedProject);
      
      // Go back to the project details view
      setView('details');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Unexpected error in handleSaveParticipants:', err);
      setError('Failed to save participants. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (view) {
      case 'create':
        return (
          <ProjectForm 
            onSubmit={handleSubmitProject} 
            onCancel={goToList}
          />
        );
      case 'edit':
        return (
          <ProjectForm
            project={selectedProject}
            onSubmit={handleSubmitProject}
            onCancel={goToList}
          />
        );
      case 'details':
        return (
          <ProjectDetails
            project={selectedProject}
            user={user}
            onClose={goToList}
            onEdit={handleEditProject}
            onManageParticipants={handleManageParticipants}
          />
        );
      case 'participants':
        return (
          <ParticipantManagement 
            project={selectedProject} 
            onClose={() => setView('details')} 
            onSave={handleSaveParticipants}
          />
        );
      case 'list':
      default:
        return (
          <>
            <Flex justifyContent="space-between" alignItems="center" marginBottom="1.5rem">
              <Flex direction="column">
                <Heading level={4}>Your Projects</Heading>
              </Flex>
              <Button variation="primary" onClick={handleCreateProject}>
                Create New Project
              </Button>
            </Flex>
            
            <ProjectList 
              projects={projects} 
              loading={loading} 
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
              onManageParticipants={handleManageParticipants}
              onViewProject={handleViewProject}
              onExportData={handleExportData}
            />
          </>
        );
    }
  };

  return (
    <View padding="1.5rem">
      <Flex direction="column" gap="1.5rem">
        <Heading level={3}>Admin Dashboard</Heading>
        
        {error && (
          <Alert variation="error" isDismissible={true} onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variation="success" isDismissible={true} onDismiss={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {renderContent()}
      </Flex>
    </View>
  );
};

export default AdminDashboard;
